import { useState } from 'react';
import { api } from '../../../lib/api';
import { useContentManager } from '../../../hooks/useContentManager';
import type { ContentItem } from '../../../hooks/useContentManager';

export default function BrochureManager() {
  const { getContentItem, updateContentItem, reloadContent } = useContentManager();

  const brochuresItem = getContentItem('site', 'brochures');
  const brochures: { title: string; url: string; created_at?: string }[] = Array.isArray(brochuresItem?.metadata?.brochures)
    ? (brochuresItem!.metadata.brochures as any[])
    : [];

  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) {
      alert('Please select a PDF file');
      return;
    }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Only PDF format is supported');
      return;
    }
    if (!title.trim()) {
      alert('Please enter a title');
      return;
    }

    try {
      setIsUploading(true);
      const data = await api.upload('/media/upload', file);
      if (!data.success) throw new Error(data.error || 'Upload failed');
      const publicUrl = data.url;

      const next = [
        ...brochures,
        { title: title.trim(), url: publicUrl, created_at: new Date().toISOString() }
      ];

      const payload: ContentItem = {
        id: brochuresItem?.id || `temp-site-brochures-${Date.now()}`,
        page: 'site',
        section: 'brochures',
        type: 'list',
        content: 'Brochures',
        order: 0,
        metadata: {
          ...(brochuresItem?.metadata || {}),
          brochures: next
        }
      } as any;

      await updateContentItem(payload);
      await reloadContent();
      setTitle('');
      setFile(null);
      alert('Brochure uploaded successfully');
    } catch (e: any) {
      console.error(e);
      alert(e?.message || 'An error occurred during upload');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (url: string) => {
    if (!confirm('Are you sure you want to delete this brochure?')) return;
    try {
      const next = brochures.filter((b) => b.url !== url);
      const payload: ContentItem = {
        id: brochuresItem?.id || `temp-site-brochures-${Date.now()}`,
        page: 'site',
        section: 'brochures',
        type: 'list',
        content: 'Brochures',
        order: 0,
        metadata: {
          ...(brochuresItem?.metadata || {}),
          brochures: next
        }
      } as any;
      await updateContentItem(payload);
      await reloadContent();
    } catch (e) {
      console.error(e);
      alert('Failed to delete brochure');
    }
  };

  const setAsDefaultBrochure = async (url: string) => {
    try {
      const contactIcons = getContentItem('home', 'contact-icons');
      const payload: ContentItem = {
        id: contactIcons?.id || `temp-home-contact-icons-${Date.now()}`,
        page: 'home',
        section: 'contact-icons',
        type: 'icons',
        content: '',
        order: 0,
        metadata: {
          ...(contactIcons?.metadata || {}),
          pdf_url: url
        }
      } as any;
      await updateContentItem(payload);
      await reloadContent();
      alert('Default brochure updated');
    } catch (e) {
      console.error(e);
      alert('Failed to update setting');
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <i className="ri-file-pdf-line mr-2"></i>
          Upload Catalog / Brochure
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Golden Tower Brochure"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">PDF File</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
            />
          </div>
          <div className="md:col-span-1 flex items-end">
            <button
              onClick={handleUpload}
              disabled={isUploading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <i className="ri-list-check mr-2"></i>
            Current Brochures
          </h3>
        </div>

        {brochures.length === 0 ? (
          <p className="text-gray-600">No brochures uploaded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Link</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {brochures.map((b, idx) => (
                  <tr key={`${b.url}-${idx}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">{b.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <a href={b.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Open</a>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(b.url);
                          alert('Link copied to clipboard');
                        }}
                        className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 cursor-pointer"
                      >
                        Copy Link
                      </button>
                      <button
                        onClick={() => setAsDefaultBrochure(b.url)}
                        className="px-3 py-1 border border-green-600 text-green-700 rounded hover:bg-green-50 cursor-pointer"
                      >
                        Set as Default
                      </button>
                      <button
                        onClick={() => handleDelete(b.url)}
                        className="px-3 py-1 border border-red-600 text-red-700 rounded hover:bg-red-50 cursor-pointer"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
